"use client";

import React from "react";
import Image from "next/image";
import s from "../styles.parts";
import Consts from "@common/config/consts";
import { dayjs, logjs } from "@lib/client";
import { usePartsList } from "../usePartsList";

const log = new logjs("PartsLabelListOrderConfArea");

/**
 * 部品ラベルリスト注文確認表示コンポーネント
 * partslabellist_orderconf.ctpをReactに移植した表示専用コンポーネント

 */
export default function PartsLabelListOrderConfArea({
  data,
  state,
  actions,
  zaikoForm,
  setZaikoForm,
  quote_number,
  photoDialogProps,
  fileupDialogProps,
  qaDialogProps,
  qaabDialogProps,
  resPict = {},
  resFile = {},
  prMySeiyaku: prMySeiyakuProp = {},
  prSeiyaku: prSeiyakuProp = {},
}) {
  log.debug("START data ", data);

  // 共通関数の取得
  const [form, formProps] = usePartsList({ AqbData: data?.AqbData ?? {} }, state, actions);
  const partskind = state?.initData?.partskind || [];

  // data を優先し、なければ props を使う（他画面と揃える）
  const prSeiyaku = data?.prSeiyaku || prSeiyakuProp || {};
  const prMySeiyaku = data?.prMySeiyaku || prMySeiyakuProp || {};

  // getHacchuStatusWithNoneを使用（bgcolor: "none"版）
  const getHacchuStatus = formProps.getHacchuStatusWithNone;

  // 現車と紐付いている場合は色変える
  const isGensha = data?.answer?.[0]?.GenshaID ? true : false;

  // 見積期限が現在時刻以降かどうか
  const _Request_Limit = data?.request?.Request_Limit;
  const isRequestLimitValid = _Request_Limit && (dayjs(_Request_Limit).isAfter(dayjs()) || dayjs(_Request_Limit).isSame(dayjs(), "day"));

  // 依頼期限の判定
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(_Request_Limit);
  log.debug(`isAfterCarryTaxinDate: ${isAfterCarryTaxinDate} _Request_Limit: ${_Request_Limit}`, data?.request);

  if (!data || !data.partsAnswers) {
    return null;
  }

  const partsAnsList = data?.partsAnswers || [];

  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報
        {isRequestLimitValid && (
          <>
            <s.QuestionButton
              className="important-btn2 qaOpen"
              data-key={partsAnsList?.[0]?.id || ""}
              onClick={(e) => {
                e.preventDefault();
                qaDialogProps.open({
                  mitsumoriNo: quote_number,
                  isQuestion: state.user?.Gyoshu_flg === 1,
                  request: data?.request,
                  answer: data?.answer,
                });
              }}
            >
              依頼元へ質問を行う | 回答を見る
            </s.QuestionButton>
            {isGensha && (
              <s.StockedVehicleButton style={{ verticalAlign: "middle", marginTop: "-5px" }} className="important-btn4">
                <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={20} height={20} />
                入庫済みの車輌があります
              </s.StockedVehicleButton>
            )}
          </>
        )}
      </s.PartsTitle>

      {isAfterCarryTaxinDate && <strong style={{ color: "#0000ff", fontSize: "12pt" }}>　※送料は「税別」にて記載してください。</strong>}

      {data?.answer?.[0]?.UserName && (
        <s.FinalAnswererContainer>
          <s.FinalAnswererLabel>最終回答者： {data.answer[0].UserName || ""} 様</s.FinalAnswererLabel>
        </s.FinalAnswererContainer>
      )}

      <s.PLTable>
        <tbody>
          {(() => {
            const rows = [];

            for (let i = 0; i < partsAnsList.length; i++) {
              const partsItem = partsAnsList[i];
              log.debug("partsItem", partsItem);
              const PA_PRID = partsItem?.PRID; // partsItem?.PartsAnswer?.PRID
              const PA_PAID = partsItem?.id; // partsItem?.PartsAnswer?.id
              const isPict = partsItem.isPict;
              const isFile = partsItem.isFile;

              // PA_PRID == 0 かつ StanderdPrice が空の場合はスキップ
              if (PA_PRID === 0 && !partsItem?.StanderdPrice) {
                // partsItem?.PartsAnswer?.StanderdPrice
                continue;
              }

              // 他社成約の判定
              let thCol = 15;
              let thColor = "";
              let showOtherCompany = false;

              if (PA_PRID > 0) {
                const hacchuflg = prMySeiyaku[PA_PRID];
                const seiyaku = prSeiyaku[PA_PRID];
                if (
                  seiyaku &&
                  hacchuflg !== Consts.HACCHU_FLG.KAKUTEI &&
                  hacchuflg !== Consts.HACCHU_FLG.HASOZUMI &&
                  hacchuflg !== Consts.HACCHU_FLG.NEBIKI &&
                  hacchuflg !== Consts.HACCHU_FLG.HENPIN
                ) {
                  thCol = 13;
                  thColor = "border-left-style:none; background:#696969; color: #FFF;";
                  showOtherCompany = true;
                }
              }

              const PartsNameHeader = isGensha ? s.PartsNameHeaderGensha : s.PartsNameHeaderNormal;

              const Bikou = data.partsRList.find((item) => item.id === partsItem.PRID)?.Bikou;

              // 部品名ヘッダー行
              rows.push(
                <React.Fragment key={`header-${i}`}>
                  <tr>
                    {showOtherCompany ? (
                      <s.PartsNameHeaderGensha
                        colSpan="2"
                        scope="row"
                        style={{ borderRightStyle: "none", background: "#696969", textAlign: "center", verticalAlign: "middle" }}
                      >
                        <Image src="/images/tasha.png" alt="他社成約" width={120} height={34} />
                      </s.PartsNameHeaderGensha>
                    ) : null}
                    <PartsNameHeader
                      colSpan={thCol}
                      scope="row"
                      style={
                        thColor
                          ? {
                              borderLeftStyle: "none",
                              background: "#696969",
                              color: "#FFF",
                            }
                          : undefined
                      }
                    >
                      {PA_PRID === 0 ? (
                        <>
                          {partsItem?.OthPartsName || ""} {/* partsItem?.PartsAnswer?.OthPartsName */}
                          {partsItem?.OthPartsSonota && <>　品名：{partsItem.OthPartsSonota}</>} {/* partsItem?.PartsAnswer?.OthPartsSonota */}
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              columnGap: "8px",
                            }}
                          >
                            <span>{partsItem?.P_PartsName}</span>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                marginTop: "5px",
                              }}
                            >
                              {/* 部品カテゴリアイコン */}
                              {(() => {
                                let pk3flg = 0;
                                const pkids = [partsItem?.P_PKID1, partsItem?.P_PKID2, partsItem?.P_PKID3, partsItem?.P_PKID4, partsItem?.P_PKID5];

                                return (
                                  <>
                                    {pkids.map((pkid, idx) => {
                                      if (!pkid || pkid === 3) {
                                        if (pkid === 3) pk3flg = 1;
                                        return null;
                                      }
                                      const categoryName = formProps.getCategoryName(pkid, partskind);
                                      const categoryStyle = formProps.getCategoryStyle(pkid);
                                      return (
                                        <s.PartsCategoryIcon key={idx} style={categoryStyle}>
                                          {categoryName}
                                        </s.PartsCategoryIcon>
                                      );
                                    })}
                                    {pk3flg === 1 && (
                                      <>
                                        <span style={{ marginLeft: "10px" }}>　　</span>
                                        <s.PartsCategoryIcon style={formProps.getCategoryStyle(3)}>{formProps.getCategoryName(3, partskind)}</s.PartsCategoryIcon>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          {Bikou ? <p style={{ margin: "5px 0 0 0", fontSize: "14px", fontWeight: "normal" }}>({Bikou})</p> : <p></p>}
                        </>
                      )}
                    </PartsNameHeader>
                  </tr>

                  {/* テーブルヘッダー行 */}
                  <tr>
                    <s.PLHeadTh2>&nbsp;</s.PLHeadTh2>
                    <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                    <s.PLHeadTh2>数量</s.PLHeadTh2>
                    <s.PLHeadTh2>部品カテゴリ</s.PLHeadTh2>
                    <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                    {isAfterCarryTaxinDate ? (
                      <s.DeliveryHeaderTaxEx>送料(税別)</s.DeliveryHeaderTaxEx>
                    ) : (
                      <s.PLHeadTh2>
                        送料
                        <span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                      </s.PLHeadTh2>
                    )}
                    <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                    <s.PLHeadTh2>見積有効期限</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                    <s.PLHeadTh2>写真</s.PLHeadTh2>
                    <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                    <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                    <s.PLHeadTh2>在庫確認期限</s.PLHeadTh2>
                    <s.PLHeadTh2>在庫確認</s.PLHeadTh2>
                  </tr>

                  {/* 回答データ行 */}
                  {(() => {
                    let hasDisplay = false;
                    const dataRows = [];
                    let j = i;

                    for (; j < partsAnsList.length; j++) {
                      const item = partsAnsList[j];
                      if (item?.StanderdPrice) {
                        // item?.PartsAnswer?.StanderdPrice
                        hasDisplay = true;

                        const paid = item.id; // item.PartsAnswer.id
                        const dataLimit = formProps.getLaterLimit(data?.request?.Request_Limit, item?.Sell_Limit);
                        const btNameQab = formProps.getQabButtonNameWithAqb(paid, dataLimit);
                        const hacchuFlg = item.Hacchu_flg; // item.PartsAnswer.Hacchu_flg
                        const { jyotai, bgcolor, backgroundColor } = getHacchuStatus(hacchuFlg);
                        const ansLimitBgColor = formProps.getAnsLimitBgColor(hacchuFlg);
                        const zaikoConfirm = formProps.getZaikoConfirmContent(hacchuFlg, bgcolor, j, setZaikoForm);

                        // 価格データ（partsAnsList[i][0]という構造を想定）
                        // PHPテンプレートでは partsAnsList[$i][0]['PartsPrice'] と partsAnsList[$i][0]['Soryo'] を使用
                        // partsAnsList[i]が配列で最初の要素にPartsPrice/Soryoがある場合、または直接PartsAnswerにStanderdPrice/DeliveryPriceがある場合を考慮
                        let partsPrice = "";
                        let soryo = 0;

                        if (Array.isArray(item) && item[0]) {
                          log.debug(`item 1 `);
                          // partsAnsList[i][0]の構造
                          partsPrice = item[0]?.PartsPrice || "";
                          soryo = item[0]?.Soryo !== undefined ? item[0].Soryo : 0;
                        } else if (item?.StanderdPrice) {
                          log.debug(`item 2 `);
                          // item?.PartsAnswer?.StanderdPrice
                          // 直接PartsAnswerにStanderdPriceがある場合
                          partsPrice = item.StanderdPrice; // item.PartsAnswer.StanderdPrice
                          soryo = item?.Soryo !== undefined ? item.Soryo : 0; // item?.PartsAnswer?.DeliveryPrice
                        } else if (item?.Price) {
                          log.debug(`item 3 `);
                          // item?.PartsAnswer?.Price
                          // PartsAnswer.Priceがある場合
                          partsPrice = item.Price; // item.PartsAnswer.Price
                          soryo = item?.Soryo !== undefined ? item.Soryo : item?.DeliveryPrice || 0; // item?.PartsAnswer?.Soryo, item?.PartsAnswer?.DeliveryPrice
                        }

                        log.debug(`partsPrice: ${partsPrice} soryo: ${soryo} item: ${JSON.stringify(item)}`);

                        dataRows.push(
                          <tr key={`data-${j}`} className={bgcolor || undefined}>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                {jyotai}
                                {jyotai && <br />}
                                {btNameQab && (
                                  <Image
                                    src={`/images/${btNameQab}.png`}
                                    className="qabOpen"
                                    data-key={`${data.answer?.[0]?.RequestAnswer?.id}/${paid}`}
                                    style={{ cursor: "pointer" }}
                                    alt="在庫確認"
                                    width={74}
                                    height={64}
                                    onClick={() => {
                                      // 在庫確認ダイアログを開く処理（必要に応じて実装）
                                      console.log("在庫確認ダイアログを開く", `${data.answer?.[0]?.RequestAnswer?.id}/${paid}`);
                                      qaabDialogProps.open({
                                        mitsumoriNo: quote_number,
                                        raid: partsItem?.RAID || null,
                                        paid: partsItem?.id || null,
                                        isQuestion: state.user?.Gyoshu_flg === 2,
                                      });
                                    }}
                                  />
                                )}
                              </div>
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell scope="row" style={{ backgroundColor: backgroundColor }}>
                              {item?.PartsJyunseiCode || ""}
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{item?.Suryo || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{formProps.getCategoryName(item?.PartsKindID, data.partskind)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{partsPrice ? `${formProps.formatNumber(partsPrice)}円` : ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{soryo == 0 ? "送料込" : soryo ? `${formProps.formatNumber(soryo)}円` : ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{formProps.getHassoStatusText(item?.Hasso)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{formProps.formatDate(item?.Sell_Limit) || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{item?.ColorNo || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>{item?.ColorName || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: backgroundColor }}>
                              {(() => {
                                const mitsumoriNo = quote_number;
                                const seqnum = partsItem?.Seqnum;
                                const PRID = partsItem?.PRID;

                                if (PA_PRID === 0) {
                                  return (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                      {isPict && (
                                        <s.FileIcon
                                          src="/images/camera_icon2.png"
                                          alt="写真"
                                          onClick={() =>
                                            photoDialogProps.open({
                                              mitsumoriNo: mitsumoriNo,
                                              prid: 0,
                                              seqnum: seqnum,
                                              refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                            })
                                          }
                                        />
                                      )}
                                      {isFile && (
                                        <s.FileIcon
                                          src="/images/file_icon.png"
                                          alt="ファイル"
                                          onClick={formProps.handleFileupClick(fileupDialogProps, mitsumoriNo, 0, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                          style={{ cursor: "pointer" }}
                                        />
                                      )}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                      {isPict && (
                                        <s.FileIcon
                                          src="/images/camera_icon2.png"
                                          alt="写真"
                                          onClick={() =>
                                            photoDialogProps.open({
                                              mitsumoriNo: mitsumoriNo,
                                              prid: PRID,
                                              seqnum: seqnum,
                                              refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                            })
                                          }
                                        />
                                      )}

                                      {isFile && (
                                        <s.FileIcon
                                          src="/images/file_icon.png"
                                          alt="ファイル"
                                          onClick={formProps.handleFileupClick(fileupDialogProps, mitsumoriNo, PRID, seqnum, Consts.WRECKER_PHOTO.YUKO_REF)}
                                          style={{ cursor: "pointer" }}
                                        />
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ whiteSpace: "pre-line", textAlign: "left", backgroundColor: backgroundColor }}>{item?.Bikou || ""}</s.ConfirmTableCell>
                            {bgcolor ? (
                              <s.ConfirmTableCell scope="row" style={{ backgroundColor: backgroundColor }}>
                                {item?.JishaNo || ""}
                              </s.ConfirmTableCell>
                            ) : (
                              <s.CompanyCodeCellBg scope="row">
                                {item?.JishaNo || ""}
                                <br />
                                {item?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                                {item?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                              </s.CompanyCodeCellBg>
                            )}
                            <s.ConfirmTableCell style={{ backgroundColor: ansLimitBgColor }}>{formProps.formatDateWithTime(item?.Ans_Limit) || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ backgroundColor: zaikoConfirm.bgColor || "" }}>
                              {/* is_standard の radio は row 非対応のため、ラッパーで縦並び＋ボタンと文字の縦中央揃え */}
                              <s.ZaikoRadioWrap>{zaikoConfirm.content}</s.ZaikoRadioWrap>
                            </s.ConfirmTableCell>
                          </tr>,
                        );
                      }

                      // グループの終了判定
                      const nextItem = partsAnsList[j + 1];
                      if (
                        j + 1 >= partsAnsList.length ||
                        (PA_PRID === 0 && item?.OthPartsName !== nextItem?.OthPartsName) || // item?.PartsAnswer?.OthPartsName
                        (PA_PRID !== 0 && PA_PRID !== nextItem?.PRID) // nextItem?.PartsAnswer?.PRID
                      ) {
                        // 未回答行の表示（PHPテンプレートでは15列）
                        if (!hasDisplay && PA_PRID !== 0) {
                          dataRows.push(
                            <s.NoneRow key={`none-${i}`}>
                              <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                              <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                              <s.ConfirmTableCell>-</s.ConfirmTableCell>
                            </s.NoneRow>,
                          );
                        }

                        i = j;
                        break;
                      }
                    }

                    return dataRows;
                  })()}
                </React.Fragment>,
              );
            }

            return rows;
          })()}
        </tbody>
      </s.PLTable>
    </>
  );
}
